import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CURSOR_PARAM, getPaginationStateFromSearchParams, PAGE_PARAM, serializeWithPagination } from "./usePaginatedTableSearchParams";

interface PaginationResultIsLoadingOrIsIsError {
  data?: undefined;
  numberOfPages?: number;
  totalCount?: number;
  isCountLoading?: boolean;
  isFetching?: boolean;
  next: () => void;
  updateQuery: (newQuery: any) => void;
  prev: () => void;
  currentPage: number;
  isLoading: boolean;
  isSuccess: false;
  isError: boolean;
  error?: any;
}
interface PaginationResultIsLoading<T> {
  data: T[];
  numberOfPages: number;
  totalCount?: number;
  isCountLoading?: boolean;
  isFetching?: boolean;
  next: () => void;
  updateQuery: (newQuery: any) => void;
  prev: () => void;
  currentPage: number;
  isLoading: boolean;
  isSuccess: true;
  isError: boolean;
  error?: any;
}

type Props = {
  useGetDataQuery: any;
  useCountData?: any;
  initQuery?: any;
  initPageTokens?: string[];
  initCurrentIndex?: number;
  syncKey?: string;
  onPaginationStateChange?: (state: {
    currentPage: number;
    currentIndex: number;
    pageTokens: string[];
  }) => void;
  getCursorId?: (item: any) => string;
  paginationMode?: "cursor" | "offset";
  skip?: boolean;
};

const arePageTokensEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  return left.every((token, index) => token === right[index]);
};

const getPaginationSignature = (currentIndex: number, pageTokens: string[]) =>
  JSON.stringify({ currentIndex, pageTokens });

const usePagination = <T extends { id: string }>({
  useGetDataQuery,
  useCountData,
  initQuery,
  initPageTokens,
  initCurrentIndex,
  syncKey,
  onPaginationStateChange,
  getCursorId,
  paginationMode = "offset",
  skip = false,
}: Props): PaginationResultIsLoadingOrIsIsError | PaginationResultIsLoading<T> => {
  const PAGE_SIZE = 10;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasExternalPaginationControl =
    typeof initCurrentIndex === "number" ||
    typeof syncKey === "string" ||
    typeof onPaginationStateChange === "function" ||
    typeof initPageTokens !== "undefined";
  const fallbackPaginationState = useMemo(() => getPaginationStateFromSearchParams(searchParams), [searchParams]);
  const resolvedInitPageTokens = useMemo(
    () => initPageTokens || (!hasExternalPaginationControl ? fallbackPaginationState.pageTokens : []),
    [fallbackPaginationState.pageTokens, hasExternalPaginationControl, initPageTokens]
  );
  const resolvedInitCurrentIndex = useMemo(
    () =>
      typeof initCurrentIndex === "number"
        ? initCurrentIndex
        : !hasExternalPaginationControl
        ? fallbackPaginationState.currentIndex
        : 0,
    [fallbackPaginationState.currentIndex, hasExternalPaginationControl, initCurrentIndex]
  );
  const fallbackSyncKey = useMemo(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(PAGE_PARAM);
    nextParams.delete(CURSOR_PARAM);
    return `${location.pathname}?${nextParams.toString()}`;
  }, [location.pathname, searchParams]);
  const resolvedSyncKey = syncKey || (!hasExternalPaginationControl ? fallbackSyncKey : undefined);

  const [pagesTokens, setPagesTokens] = useState<string[]>(resolvedInitPageTokens);
  const [currentIndex, setCurrentIndex] = useState<number>(resolvedInitCurrentIndex);
  const [query, setQuery] = useState<any>(initQuery || {});
  const [stableData, setStableData] = useState<T[] | undefined>(undefined);
  const currentIndexRef = useRef<number>(resolvedInitCurrentIndex);
  const pagesTokensRef = useRef<string[]>(resolvedInitPageTokens);
  const latestInitQueryRef = useRef<any>(initQuery || {});
  const lastHandledSyncKeyRef = useRef<string | undefined>(resolvedSyncKey);
  const lastSeenExternalPaginationRef = useRef<string>(
    getPaginationSignature(resolvedInitCurrentIndex, resolvedInitPageTokens)
  );
  const lastPublishedPaginationRef = useRef<string>(
    getPaginationSignature(resolvedInitCurrentIndex, resolvedInitPageTokens)
  );
  const externalPaginationSignature = useMemo(
    () => getPaginationSignature(resolvedInitCurrentIndex, resolvedInitPageTokens),
    [resolvedInitCurrentIndex, resolvedInitPageTokens]
  );
  const {
    isLoading: isLoadingData,
    isFetching: isFetchingData = false,
    isSuccess: isSuccessData,
    data,
    isError: isErrorData,
    error: errorData,
  } = useGetDataQuery({
    where: query,
    cursor:
      paginationMode === "cursor" && pagesTokens[currentIndex - 1]
        ? { docId: pagesTokens[currentIndex - 1] }
        : undefined,
    offset: paginationMode === "offset" ? pagesTokens[currentIndex - 1] : undefined,
    limit: PAGE_SIZE,
  }, { skip });

  // Count query (single call). RTKQ-generated hooks accept (arg, options) and ignore unknown options safely.
  const countResult = useCountData ? (useCountData as any)({ where: query }, { skip }) || {} : {};
  const {
    isLoading: isLoadingCount = false,
    isFetching: isFetchingCount = false,
    isSuccess: isSuccessCount = false,
    data: count,
    isError: isErrorCount = false,
    error: errorCount,
  } = countResult;

  const totalCount = typeof count === "number" ? count : undefined;

  const updateQuery = async (newQuery: any) => {
    setQuery(newQuery);
    setPagesTokens([]);
    setCurrentIndex(0);
  };

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    pagesTokensRef.current = pagesTokens;
  }, [pagesTokens]);

  useEffect(() => {
    latestInitQueryRef.current = initQuery || {};
  }, [initQuery]);

  useEffect(() => {
    if (!resolvedSyncKey) return;
    const syncKeyChanged = lastHandledSyncKeyRef.current !== resolvedSyncKey;
    const externalPaginationChanged = lastSeenExternalPaginationRef.current !== externalPaginationSignature;
    const localMatchesDesired =
      currentIndexRef.current === resolvedInitCurrentIndex &&
      arePageTokensEqual(pagesTokensRef.current, resolvedInitPageTokens);

    if (syncKeyChanged) {
      lastHandledSyncKeyRef.current = resolvedSyncKey;
      lastSeenExternalPaginationRef.current = externalPaginationSignature;
      lastPublishedPaginationRef.current = externalPaginationSignature;
      setStableData(undefined);
      setQuery(latestInitQueryRef.current);
      setPagesTokens(resolvedInitPageTokens);
      setCurrentIndex(resolvedInitCurrentIndex);
      return;
    }

    if (!externalPaginationChanged) return;

    lastSeenExternalPaginationRef.current = externalPaginationSignature;

    if (localMatchesDesired) {
      lastPublishedPaginationRef.current = externalPaginationSignature;
      return;
    }

    const isInternalPaginationEcho = lastPublishedPaginationRef.current === externalPaginationSignature;
    if (isInternalPaginationEcho) return;

    lastPublishedPaginationRef.current = externalPaginationSignature;
    setPagesTokens(resolvedInitPageTokens);
    setCurrentIndex(resolvedInitCurrentIndex);
  }, [externalPaginationSignature, resolvedInitCurrentIndex, resolvedInitPageTokens, resolvedSyncKey]);

  useEffect(() => {
    if (data && data.length === 0 && currentIndex > 0) {
      // If a page becomes empty (e.g., due to filtering or deletions),
      // drop its token and step back one page.
      setPagesTokens((prev) => [...prev.slice(0, currentIndex), ...prev.slice(currentIndex + 1)]);
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }
  }, [data, currentIndex]);

  useEffect(() => {
    if (!isSuccessData) return;
    setStableData(data);
  }, [data, isSuccessData]);

  // Ensure the token for navigating FROM the current page is always set based on current data
  // This ensures that when we navigate forward, we always have a fresh token
  useEffect(() => {
    if (data && data.length > 0 && currentIndex >= 0 && isSuccessData && !isFetchingData) {
      const lastItem = data[data.length - 1];
      const token = getCursorId ? getCursorId(lastItem) : lastItem.id;
      
      // The token at pagesTokens[currentIndex] is used to navigate FROM current page TO next page
      // Update it if it doesn't exist or is different
      setPagesTokens(prev => {
        if (prev[currentIndex] === token) return prev; // No change needed
        
        const updated = [...prev];
        // Ensure array is long enough
        while (updated.length <= currentIndex) {
          updated.push('');
        }
        updated[currentIndex] = token;
        return updated;
      });
    }
  }, [data, currentIndex, getCursorId, isFetchingData, isSuccessData]);

  useEffect(() => {
    if (skip) return;

    const paginationState = {
      currentPage: currentIndex + 1,
      currentIndex,
      pageTokens: pagesTokens,
    };
    lastPublishedPaginationRef.current = getPaginationSignature(currentIndex, pagesTokens);

    if (onPaginationStateChange) {
      onPaginationStateChange(paginationState);
      return;
    }

    if (hasExternalPaginationControl) return;

    const nextParams = serializeWithPagination(new URLSearchParams(searchParams), paginationState);
    if (nextParams.toString() === searchParams.toString()) return;
    setSearchParams(nextParams, { replace: true });
  }, [currentIndex, hasExternalPaginationControl, onPaginationStateChange, pagesTokens, searchParams, setSearchParams, skip]);

  const getNextPage = () => {
    const newPageIndex = currentIndex + 1;

    // If count isn't loaded yet, don't block UI; allow next only when the current page is "full"
    if (!isSuccessCount) {
      if (!data || data.length < PAGE_SIZE) return;
    } else {
      if (typeof totalCount === "number" && newPageIndex > Math.ceil(totalCount / PAGE_SIZE)) return;
    }

    if (!data || data.length === 0) return;

    const lastItem = data[data.length - 1];
    const token = getCursorId ? getCursorId(lastItem) : lastItem.id;

    // `pagesTokens[i]` is the token used to fetch page (i + 2),
    // because page N uses `pagesTokens[N - 2]` as its offset/cursor.
    // So when we're on `currentIndex`, we must store the token at `pagesTokens[currentIndex]`
    // (token derived from the last item on the current page).
    //
    // Also, if the user navigated backwards, tokens after `currentIndex` may be stale.
    // Truncate them so forward navigation re-derives them from fresh data.
    setPagesTokens((prev) => {
      const next = prev.slice(0, currentIndex + 1);
      next[currentIndex] = token;
      return next;
    });
    setCurrentIndex(newPageIndex);
  };

  const getPrevPage = () => {
    if (currentIndex === 0) return;
    setCurrentIndex(currentIndex - 1);
  };

  const visibleData = isSuccessData ? data : stableData;
  const hasVisibleData = typeof visibleData !== "undefined";
  const hasQueryError = isErrorData || (useCountData ? isErrorCount : false);

  return {
    isError: hasQueryError,
    error: errorData || (useCountData ? errorCount : undefined),
    isLoading: isLoadingData,
    isFetching: isFetchingData,
    // Render as soon as the main list data is ready; count can load in the background.
    isSuccess: !hasQueryError && (isSuccessData || hasVisibleData),
    data: visibleData,
    totalCount,
    isCountLoading: useCountData ? isLoadingCount || isFetchingCount : false,
    // If count hasn't loaded yet, keep Next enabled when we have a "full" page.
    // This makes pagination usable even when count is slow.
    numberOfPages: isSuccessCount
      ? Math.ceil((typeof count === "number" ? count : 0) / PAGE_SIZE)
      : currentIndex + 1 + (isSuccessData && data && data.length === PAGE_SIZE ? 1 : 0),
    next: () => getNextPage(),
    updateQuery: (newQuery: any) => updateQuery(newQuery),
    prev: () => getPrevPage(),
    currentPage: currentIndex + 1,
  };
};

export default usePagination;
